// Imports
import { faAt, faPhone } from '@fortawesome/pro-regular-svg-icons';
import capitalize from 'lodash/capitalize';
import styled from 'styled-components/macro';
import { GetCommitteesResponse } from '../../../../../../shareables/types/api/main/website/page/type/209/committees/get';
import formatPhoneNumber from '../../../../../../shareables/utils/format-phone-number';
import ContactInfo, { ContactDetails } from '../shared-components/contact-info';
import ProfilePhoto from '../../../../shared-components/profile-photo';
import UserAvatar from '../../../../shared-components/user-avatar';
import { useAppSelector } from '../../../../../../shareables/foundation/front-end/redux/hooks';
import SignInAnchor from '../../../../sign-in-anchor';


// Styled components
const CommitteeMember = styled.div`
	display: flex;
	align-items: center;
	gap: 0.5rem;
	
	& > div > div {
		margin-bottom: -0.1875rem;
	}
	
	@media only screen and (max-width: 31.25rem) {
		img {
			display: none;
		}
	}
`;

const ProfileAvatar = styled(UserAvatar)`
	display: flex;
	align-items: center;
	justify-content: center;
	
	height: 3.125rem;
	width: 3.125rem;
	font-size: 1.5rem;
	
	box-shadow: 0 0 0.25rem rgba(0, 0, 0, 0.1);
	
	@media only screen and (max-width: 31.25rem) {
		display: none;
	}
`;


// Define the accepted props
interface Props {
	committeeMember: GetCommitteesResponse[0]['members'][0];
}


// Function component
const CommitteeMembersRow: React.FC<Props> = ({ committeeMember }) => {
	// Use Redux functionality
	const website = useAppSelector((state) => state.self.website);
	
	
	// Return JSX
	return (
		<tr>
			<td>
				<CommitteeMember>
					{committeeMember.profilePhotoURL ? (
						<ProfilePhoto size='3.125rem' alt='Profile photo' src={committeeMember.profilePhotoURL} />
					) : (
						<ProfileAvatar
							aria-label='Profile photo'
							firstName={committeeMember.firstName}
							lastName={committeeMember.lastName}
							round
						/>
					)}
					
					<div>
						<div>
							{committeeMember.firstName} {committeeMember.lastName}
						</div>
						{committeeMember.designation && (
							<small className='is-gray'>
								<em>{capitalize(committeeMember.designation.replace(/_/g, ' '))}</em>
							</small>
						)}
					</div>
				</CommitteeMember>
			</td>
			
			<td>
				{!committeeMember.email && !committeeMember.phone && !committeeMember.missingReason && <em>None</em>}
				
				{committeeMember.missingReason === 'UNAUTHENTICATED' && (
					<em>
						<SignInAnchor>Sign in</SignInAnchor> to view
					</em>
				)}
				{committeeMember.missingReason === 'PENDING_APPROVAL' && <em>Account must be approved to view</em>}
				<ContactInfo
					contactDetails={[
						committeeMember.phone
							? {
									icon: faPhone,
									href: `tel:${committeeMember.phone}`,
									text: formatPhoneNumber(committeeMember.phone, website?.country.callingCode ?? 1),
								}
							: null,
						committeeMember.email
							? {
									icon: faAt,
									href: `mailto:${committeeMember.email}`,
									text: committeeMember.email,
								}
							: null,
					].filter((contact): contact is ContactDetails => contact !== null)}
				/>
			</td>
		</tr>
	);
};

export default CommitteeMembersRow;
